/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/Toast.tsx
 *
 * Rôle :
 *   Composant ToastViewport (à monter UNE FOIS dans AppLayout ou main.tsx).
 *   Lit la queue depuis `useToastStore`, rend chaque toast avec son icône,
 *   sa couleur et ses contrôles d'accessibilité.
 *
 *   Accessibilité (WCAG / RGAA) :
 *     - role="status" + aria-live="polite" sur les info/success.
 *     - role="alert" + aria-live="assertive" sur les warning/error.
 *     - bouton "Fermer" avec aria-label explicite.
 *
 *   Pour pousser un toast depuis n'importe où :
 *     import { toast } from '@/stores/toast';
 *     toast.success('Inscription confirmée');
 *
 * Réutilisable :
 *   100 %. Aucune dépendance au métier TNE.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { useIntl } from 'react-intl';
import { useToastStore } from '@/stores/toast';
import type { ToastItem, ToastKind } from '@/stores/toast';
import styles from './Toast.module.css';

/**
 * Viewport — empile les toasts dans le coin bas-droit. Sur mobile, étire
 * full width pour rester lisible (ajusté dans le CSS).
 *
 * @example
 *   <AppLayout>
 *     <ToastViewport />
 *     ...
 *   </AppLayout>
 */
export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  const intl = useIntl();

  return (
    <div
      className={styles.viewport}
      aria-label={intl.formatMessage({ id: 'aria.toast.viewport' })}
    >
      {items.map((item) => (
        <ToastBlock key={item.id} item={item} onClose={() => dismiss(item.id)} />
      ))}
    </div>
  );
}

interface ToastBlockProps {
  item: ToastItem;
  onClose: () => void;
}

/** Choix du rôle ARIA selon la criticité — bonne pratique RGAA. */
function ariaRoleFor(kind: ToastKind): { role: string; live: 'polite' | 'assertive' } {
  if (kind === 'error' || kind === 'warning') {
    return { role: 'alert', live: 'assertive' };
  }
  return { role: 'status', live: 'polite' };
}

/** Pictogramme texte simple — pas de dépendance icône pour rester léger. */
function iconFor(kind: ToastKind): string {
  switch (kind) {
    case 'success':
      return '✓';
    case 'info':
      return 'ℹ';
    case 'warning':
      return '⚠';
    case 'error':
      return '✕';
  }
}

function ToastBlock({ item, onClose }: ToastBlockProps) {
  const { role, live } = ariaRoleFor(item.kind);
  const cls = [styles.toast, styles[`kind_${item.kind}`]].join(' ');
  const intl = useIntl();

  return (
    <div role={role} aria-live={live} aria-atomic="true" className={cls}>
      <span className={styles.icon} aria-hidden="true">
        {iconFor(item.kind)}
      </span>
      <span className={styles.message}>{item.message}</span>
      <button
        type="button"
        onClick={onClose}
        className={styles.close}
        aria-label={intl.formatMessage({ id: 'aria.toast.dismiss' })}
      >
        ×
      </button>
    </div>
  );
}
